import type { ILabel } from 'core/utils/lmsSuspension';
import { parseJsonValue } from 'core/utils/sendgridTemplates';

/**
 * ТЗ 0085 — «Правила писем» дофинансирования: чистый слой.
 *
 * Здесь всё, что решает, КАК правило читается на экране: фраза повода,
 * «когда уйдёт», почему переключатель серый, что значит код отказа сервера.
 * Та же фраза нужна четырём местам (таблица правил, календарь, журнал писем,
 * карточки кандидата и окна) — собранная в каждом по-своему, она разошлась
 * бы на первой правке.
 *
 * Ни React, ни Apollo, ни i18n: наружу уходят описатели подписи (`ILabel`),
 * компонент разрешает их своим `t`. Локаль-гейт обходит
 * `MAIL_RULES_I18N_KEYS`, поэтому ключ без перевода валит CI.
 *
 * Коды и пределы — зеркало `dofinansowanie/constants.py` и
 * `dofinansowanie/schema/mail_rules.py`.
 */

export const MAIL_RULES_NS = 'pages.dofinansowanie.mailRules';
const MR = MAIL_RULES_NS;

export const MAIL_KIND_OPERATOR_INFO = 'operator_info';
export const MAIL_KIND_SOON = 'intake_soon';
export const MAIL_KIND_RUNNING = 'intake_running';

/** «За N дней»: 0 — в день старта, не дальше 90 (`MAIL_OFFSET_MAX`). */
export const MAIL_OFFSET_MIN = 0;
export const MAIL_OFFSET_MAX = 90;
/** «Набор идёт»: не чаще раза в 1…30 дней (`MAIL_EVERY_MAX`). */
export const MAIL_EVERY_MIN = 1;
export const MAIL_EVERY_MAX = 30;
/** Горизонт календаря по умолчанию — столько же отдаёт сервер без `days`. */
export const MAIL_CALENDAR_DAYS = 14;
/** Автоподбор (и письмо-знакомство) идёт по расписанию раз в 10 минут. */
export const AUTOMATCH_EVERY_MINUTES = 10;
/** Часы отправки — по Варшаве, целым часом. */
export const MAIL_SEND_HOURS: number[] = Array.from(
  { length: 24 },
  (_, h) => h,
);

export interface IFundingMailRule {
  id: string;
  code: string;
  kind: string;
  offsetDays?: number | null;
  everyDays?: number | null;
  templateId?: string | null;
  templateName?: string | null;
  templateSubject?: string | null;
  isActive: boolean;
  updatedAt?: string | null;
  updatedByName?: string | null;
  canDelete: boolean;
  sentCount: number;
}

export interface IFundingMailSettings {
  paused: boolean;
  fromEmail?: string | null;
  fromName?: string | null;
  replyTo?: string | null;
  effectiveFromEmail?: string | null;
  effectiveFromName?: string | null;
  effectiveReplyTo?: string | null;
  soonSendHour: number;
  runningSendHour: number;
  updatedAt?: string | null;
  updatedByName?: string | null;
  serverMailEnabled: boolean;
  testRecipient?: string | null;
  notifyScheduled: boolean;
  automatchScheduled: boolean;
  shortenerEnabled: boolean;
  canEdit: boolean;
  /** Почему автоподбор стоит: `mail_off` | `paused` | `intro_off` | `shortener_off`. */
  automatchBlocked?: string | null;
}

export interface IFundingMailVariable {
  name: string;
  descriptionRu: string;
  descriptionPl: string;
  example: string;
}

export interface IFundingMailTemplateCheck {
  success: boolean;
  templateId: string;
  name?: string | null;
  subject?: string | null;
  hasActiveVersion: boolean;
  usedVariables: string[];
  unknownVariables: string[];
  unusedVariables: string[];
  customErrors?: Array<{ code?: string | null } | null> | null;
}

export interface IFundingMailCalendarEntry {
  date: string;
  kind: string;
  ruleId: string;
  offsetDays?: number | null;
  daysLeft: number;
  intakeId: string;
  intakeStartsOn?: string | null;
  intakeEndsOn?: string | null;
  operatorId?: string | null;
  operatorName?: string | null;
  recipients: number;
  alreadySent: number;
  templateId?: string | null;
  templateName?: string | null;
  sendHour: number;
  /** Когда уйдёт на самом деле; пусто — по строке всё отправлено. */
  sendAt?: string | null;
}

// ---------------------------------------------------------------------------
// Фраза повода
// ---------------------------------------------------------------------------

export const MAIL_PHRASE_KEYS = {
  operatorInfo: `${MR}.phrase.operatorInfo`,
  soon: `${MR}.phrase.soon`,
  soonToday: `${MR}.phrase.soonToday`,
  soonTomorrow: `${MR}.phrase.soonTomorrow`,
  soonPlain: `${MR}.phrase.soonPlain`,
  running: `${MR}.phrase.running`,
  runningPlain: `${MR}.phrase.runningPlain`,
  deleted: `${MR}.phrase.deleted`,
  unknown: `${MR}.phrase.unknown`,
};

/**
 * Ключи с формами множественного числа i18next (`_one`, `_few`, `_many`,
 * `_other`): «за 21 день», «за 2 дня», «за 7 дней»; pl — «1 dzień», «2 dni».
 * Локаль-гейт проверяет каждую форму в обоих языках.
 */
export const MAIL_PLURAL_KEYS = [
  MAIL_PHRASE_KEYS.soon,
  MAIL_PHRASE_KEYS.running,
];
export const PLURAL_FORMS = ['one', 'few', 'many', 'other'] as const;

interface IRuleShape {
  kind: string;
  offsetDays?: number | null;
  everyDays?: number | null;
}

/**
 * Фраза повода — ОДНА функция на таблицу правил, календарь, журнал и
 * карточки: «Оператор подобран», «В день старта набора», «Накануне старта
 * (за 1 день)», «За 7 дней до старта набора», «Набор идёт — не чаще раза
 * в 3 дня». Число — через `count`: формы слова выбирает i18next по языку.
 */
export const mailRulePhrase = ({
  kind,
  offsetDays,
  everyDays,
}: IRuleShape): ILabel => {
  if (kind === MAIL_KIND_OPERATOR_INFO)
    return { key: MAIL_PHRASE_KEYS.operatorInfo };
  if (kind === MAIL_KIND_SOON) {
    if (offsetDays === 0) return { key: MAIL_PHRASE_KEYS.soonToday };
    if (offsetDays === 1) return { key: MAIL_PHRASE_KEYS.soonTomorrow };
    if (offsetDays === null || offsetDays === undefined) {
      return { key: MAIL_PHRASE_KEYS.soonPlain };
    }
    return { key: MAIL_PHRASE_KEYS.soon, values: { count: offsetDays } };
  }
  if (kind === MAIL_KIND_RUNNING) {
    if (everyDays === null || everyDays === undefined) {
      return { key: MAIL_PHRASE_KEYS.runningPlain };
    }
    return { key: MAIL_PHRASE_KEYS.running, values: { count: everyDays } };
  }
  return { key: MAIL_PHRASE_KEYS.unknown, values: { kind } };
};

/**
 * Фраза правила у строки журнала. Правило удалено — так и говорим: у строки
 * остались повод и шаблон снимком, но срок удалённого правила неизвестен.
 */
export const notificationRulePhrase = (row: {
  kind: string;
  ruleId?: string | null;
  ruleOffsetDays?: number | null;
  ruleEveryDays?: number | null;
}): ILabel =>
  row.ruleId
    ? mailRulePhrase({
        kind: row.kind,
        offsetDays: row.ruleOffsetDays,
        everyDays: row.ruleEveryDays,
      })
    : { key: MAIL_PHRASE_KEYS.deleted };

/**
 * Фраза строки календаря. У записи календаря нет `everyDays` — берём его у
 * правила; правила в списке нет — фраза без числа, но не выдуманная.
 */
export const calendarEntryPhrase = (
  entry: Pick<IFundingMailCalendarEntry, 'kind' | 'offsetDays' | 'ruleId'>,
  rulesById: ReadonlyMap<string, IFundingMailRule>,
): ILabel => {
  const rule = rulesById.get(entry.ruleId);
  return mailRulePhrase(
    rule || { kind: entry.kind, offsetDays: entry.offsetDays },
  );
};

// ---------------------------------------------------------------------------
// Когда уходит
// ---------------------------------------------------------------------------

/** 9 → «09:00». */
export const formatSendHour = (hour: number): string =>
  `${String(hour).padStart(2, '0')}:00`;

export const MAIL_WHEN_KEYS = {
  automatch: `${MR}.when.automatch`,
  atHour: `${MR}.when.atHour`,
};

/** Час письма этого вида: «за N дней» и «набор идёт» — свои часы. */
export const ruleSendHour = (
  kind: string,
  settings: Pick<IFundingMailSettings, 'soonSendHour' | 'runningSendHour'>,
): number | null => {
  if (kind === MAIL_KIND_SOON) return settings.soonSendHour;
  if (kind === MAIL_KIND_RUNNING) return settings.runningSendHour;
  return null;
};

/**
 * «Сразу после автоподбора (каждые 10 мин)» — у знакомства; «в 09:00 по
 * Варшаве» — у остальных. Настроек ещё нет — `null`, а не выдуманный час.
 */
export const mailRuleWhen = (
  kind: string,
  settings: Pick<
    IFundingMailSettings,
    'soonSendHour' | 'runningSendHour'
  > | null,
): ILabel | null => {
  if (kind === MAIL_KIND_OPERATOR_INFO) {
    return {
      key: MAIL_WHEN_KEYS.automatch,
      values: { n: AUTOMATCH_EVERY_MINUTES },
    };
  }
  if (!settings) return null;
  const hour = ruleSendHour(kind, settings);
  return hour === null
    ? null
    : { key: MAIL_WHEN_KEYS.atHour, values: { hour: formatSendHour(hour) } };
};

// ---------------------------------------------------------------------------
// Почему письма НЕ уйдут
// ---------------------------------------------------------------------------

export const MAIL_STATE_KEYS = {
  serverOff: `${MR}.state.blocker.serverOff`,
  paused: `${MR}.state.blocker.paused`,
  notifyOff: `${MR}.state.blocker.notifyOff`,
  automatchOff: `${MR}.state.blocker.automatchOff`,
  shortenerOff: `${MR}.state.blocker.shortenerOff`,
  testRecipient: `${MR}.state.testRecipient`,
  envHint: `${MR}.state.envHint`,
};

/**
 * Имена настроек за фактами «Состояния». На экране — словами, имя — только в
 * подсказке: менеджеру оно ничего не говорит, а администратору нужно.
 *
 * ТЗ 0093: почта, письма о наборе и автоподбор — больше не переменные
 * окружения, а главные выключатели администратора («Настройки → Роботы и
 * расписания»); сократитель по-прежнему задаётся при установке сервера.
 */
export const MAIL_ENV = {
  serverOff: 'DOFIN_MAIL_ENABLED',
  notifyOff: 'DOFIN_NOTIFY_ENABLED',
  automatchOff: 'DOFIN_AUTOMATCH_ENABLED',
  shortenerOff: 'SHORTLINK_BASE_URL',
} as const;

/**
 * Вкладка «Роботов и расписаний», где у причины главный выключатель: почта —
 * живой канал (вкладка «Почта»), письма о наборе и автоподбор — роботы.
 * Сократителя там нет: он задаётся при установке.
 */
export const MAIL_MAIN_TABS = {
  serverOff: 'mail',
  notifyOff: 'robots',
  automatchOff: 'robots',
} as const;

export type MailMainTab = typeof MAIL_MAIN_TABS[keyof typeof MAIL_MAIN_TABS];

export interface IMailBlocker {
  label: ILabel;
  /** Имя настройки — для подсказки; пусто — причина в самом разделе. */
  env: string | null;
  /** Вкладка главного выключателя; `null` — его нет (пауза, сократитель). */
  mainTab: MailMainTab | null;
}

/**
 * Что мешает письмам уйти людям — по убыванию силы: сервер без почты
 * глушит всё, пауза — всё из ERP, флаги расписания — свою половину.
 *
 * 🔴 Список, а не первое попавшееся: выключи владелец паузу, а сервер всё
 * равно молчит — и он узнал бы о второй причине только следующим утром.
 */
export const mailBlockers = (
  settings: Pick<
    IFundingMailSettings,
    | 'serverMailEnabled'
    | 'paused'
    | 'notifyScheduled'
    | 'automatchScheduled'
    | 'shortenerEnabled'
  >,
): IMailBlocker[] => {
  const out: IMailBlocker[] = [];
  if (!settings.serverMailEnabled) {
    out.push({
      label: { key: MAIL_STATE_KEYS.serverOff },
      env: MAIL_ENV.serverOff,
      mainTab: MAIL_MAIN_TABS.serverOff,
    });
  }
  if (settings.paused) {
    out.push({
      label: { key: MAIL_STATE_KEYS.paused },
      env: null,
      mainTab: null,
    });
  }
  if (!settings.notifyScheduled) {
    out.push({
      label: { key: MAIL_STATE_KEYS.notifyOff },
      env: MAIL_ENV.notifyOff,
      mainTab: MAIL_MAIN_TABS.notifyOff,
    });
  }
  if (!settings.automatchScheduled) {
    out.push({
      label: { key: MAIL_STATE_KEYS.automatchOff },
      env: MAIL_ENV.automatchOff,
      mainTab: MAIL_MAIN_TABS.automatchOff,
    });
  }
  if (!settings.shortenerEnabled) {
    out.push({
      label: { key: MAIL_STATE_KEYS.shortenerOff },
      env: MAIL_ENV.shortenerOff,
      mainTab: null,
    });
  }
  return out;
};

/**
 * Почему автоподбор операторов стоит (`automatchBlocked` сервера — та же
 * проверка, что в самой задаче): почта выключена на сервере, рассылка на
 * паузе, письмо-знакомство выключено, нет сократителя ссылок.
 */
export const AUTOMATCH_BLOCKS = [
  'mail_off',
  'paused',
  'intro_off',
  'shortener_off',
] as const;

export const AUTOMATCH_BLOCK_UNKNOWN_KEY = `${MR}.automatch.reasons.unknown`;

/** Причина словами; незнакомый код — общие слова, а не сырой код. */
export const automatchBlockLabel = (code: string | null | undefined): ILabel =>
  code && (AUTOMATCH_BLOCKS as readonly string[]).includes(code)
    ? { key: `${MR}.automatch.reasons.${code}` }
    : { key: AUTOMATCH_BLOCK_UNKNOWN_KEY };

// ---------------------------------------------------------------------------
// Почему контрол серый
// ---------------------------------------------------------------------------

export const MAIL_LOCK_KEYS = {
  readOnly: `${MR}.lock.readOnly`,
  noTemplate: `${MR}.lock.noTemplate`,
  cannotDelete: `${MR}.lock.cannotDelete`,
  serverOff: `${MR}.lock.serverOff`,
  shortenerOff: `${MR}.lock.shortenerOff`,
  testNoTemplate: `${MR}.lock.testNoTemplate`,
};

/**
 * Переключатель «вкл/выкл». Без шаблона правило не включить — сервер откажет
 * `template_required`, и лучше сказать это до клика. Выключить можно всегда.
 */
export const ruleToggleLock = (
  rule: Pick<IFundingMailRule, 'isActive' | 'templateId'>,
  canEdit: boolean,
): ILabel | null => {
  if (!canEdit) return { key: MAIL_LOCK_KEYS.readOnly };
  if (!rule.isActive && !rule.templateId)
    return { key: MAIL_LOCK_KEYS.noTemplate };
  return null;
};

export const ruleEditLock = (canEdit: boolean): ILabel | null =>
  canEdit ? null : { key: MAIL_LOCK_KEYS.readOnly };

export const ruleDeleteLock = (
  rule: Pick<IFundingMailRule, 'canDelete'>,
  canEdit: boolean,
): ILabel | null => {
  if (!canEdit) return { key: MAIL_LOCK_KEYS.readOnly };
  if (!rule.canDelete) return { key: MAIL_LOCK_KEYS.cannotDelete };
  return null;
};

/**
 * «Тестовое письмо мне». Пауза тесту не мешает (так на сервере), а
 * выключенная на сервере почта — мешает.
 */
export const ruleTestLock = (
  rule: Pick<IFundingMailRule, 'templateId'>,
  canEdit: boolean,
  serverMailEnabled: boolean,
  // Без сократителя сервер отказывает и тесту (`shortlink_disabled`), как
  // боевому письму: говорим об этом до нажатия. Не знаем — не запираем.
  shortenerEnabled = true,
): ILabel | null => {
  if (!canEdit) return { key: MAIL_LOCK_KEYS.readOnly };
  if (!serverMailEnabled) return { key: MAIL_LOCK_KEYS.serverOff };
  if (!shortenerEnabled) return { key: MAIL_LOCK_KEYS.shortenerOff };
  if (!rule.templateId) return { key: MAIL_LOCK_KEYS.testNoTemplate };
  return null;
};

// ---------------------------------------------------------------------------
// Коды отказа сервера
// ---------------------------------------------------------------------------

/** Все коды, которые умеют отдавать запросы и мутации ТЗ 0085. */
export const MAIL_ERROR_CODES = [
  'not_found',
  'cannot_create',
  'cannot_delete',
  'invalid_offset',
  'offset_taken',
  'invalid_every',
  'invalid_hour',
  'invalid_email',
  'template_required',
  'template_not_found',
  'sendgrid_unavailable',
  'sendgrid_forbidden',
  'sendgrid_rejected',
  'disabled_on_server',
  'no_email',
  'shortlink_disabled',
] as const;

export const MAIL_ERROR_FORBIDDEN_KEY = `${MR}.errors.forbidden`;
export const MAIL_ERROR_UNKNOWN_KEY = `${MR}.errors.unknown`;

const errorKey = (code: string): string => `${MR}.errors.${code}`;

/** Пределы в тексте ошибки — из тех же констант, что проверяет форма. */
const ERROR_VALUES: Record<string, Record<string, number>> = {
  invalid_offset: { min: MAIL_OFFSET_MIN, max: MAIL_OFFSET_MAX },
  invalid_every: { min: MAIL_EVERY_MIN, max: MAIL_EVERY_MAX },
};

/**
 * Код → подпись. Незнакомый код не печатается сырым: на экране «что-то
 * пошло не так», а код остаётся в сети и логах сервера.
 */
export const mailErrorLabel = (code?: string | null): ILabel => {
  if (code && (MAIL_ERROR_CODES as readonly string[]).includes(code)) {
    const values = ERROR_VALUES[code];
    return values ? { key: errorKey(code), values } : { key: errorKey(code) };
  }
  return { key: MAIL_ERROR_UNKNOWN_KEY };
};

// ---------------------------------------------------------------------------
// Календарь
// ---------------------------------------------------------------------------

export interface IMailCalendarDay {
  date: string;
  entries: IFundingMailCalendarEntry[];
}

/**
 * Внутри дня — по тому, когда письмо уйдёт НА САМОМ ДЕЛЕ (`sendAt`): строка,
 * догоняющая «ближайшим часом», встаёт после сегодняшних в час правила.
 * Всё отправлено (`sendAt` пуст) — в конец дня; без времени вовсе — по часу.
 */
const bySendTime = (
  a: IFundingMailCalendarEntry,
  b: IFundingMailCalendarEntry,
): number => {
  const ta = a.sendAt ? Date.parse(a.sendAt) : NaN;
  const tb = b.sendAt ? Date.parse(b.sendAt) : NaN;
  const hasA = !Number.isNaN(ta);
  const hasB = !Number.isNaN(tb);
  if (hasA && hasB && ta !== tb) return ta - tb;
  if (hasA !== hasB) return hasA ? -1 : 1;
  return a.sendHour - b.sendHour;
};

/** Записи по дням, внутри дня — по времени отправки. Порядок дней — сервера. */
export const groupCalendarByDate = (
  entries: ReadonlyArray<IFundingMailCalendarEntry>,
): IMailCalendarDay[] => {
  const days: IMailCalendarDay[] = [];
  const byDate = new Map<string, IMailCalendarDay>();
  entries.forEach((entry) => {
    let day = byDate.get(entry.date);
    if (!day) {
      day = { date: entry.date, entries: [] };
      byDate.set(entry.date, day);
      days.push(day);
    }
    day.entries.push(entry);
  });
  days.forEach((day) => day.entries.sort(bySendTime));
  return days;
};

export const MAIL_CALENDAR_SEND_KEYS = {
  allSent: `${MR}.calendar.allSent`,
  todayAt: `${MR}.calendar.todayAt`,
  nextRun: `${MR}.calendar.nextRun`,
  at: `${MR}.calendar.at`,
};

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * Когда письмо уйдёт НА САМОМ ДЕЛЕ (`sendAt` сервера), а не «в час отправки
 * правила»: окно подтвердили сегодня после часа — письмо уходит ближайшим
 * запуском, в тот же день. `null` — по строке всё уже отправлено.
 *
 * * сегодня в час правила — «сегодня в 09:05»;
 * * сегодня, но не в час правила — «в ближайший час, ~16:05»;
 * * другой день — «25.09 в 09:05».
 *
 * Время — местное время браузера: сотрудники в Польше, как и сервер.
 */
export const calendarSendLabel = (
  entry: Pick<IFundingMailCalendarEntry, 'sendAt' | 'sendHour'>,
  now: Date,
): ILabel => {
  if (!entry.sendAt) return { key: MAIL_CALENDAR_SEND_KEYS.allSent };
  const at = new Date(entry.sendAt);
  if (Number.isNaN(at.getTime()))
    return { key: MAIL_CALENDAR_SEND_KEYS.allSent };
  const time = `${pad2(at.getHours())}:${pad2(at.getMinutes())}`;
  if (sameDay(at, now)) {
    return at.getHours() === entry.sendHour
      ? { key: MAIL_CALENDAR_SEND_KEYS.todayAt, values: { time } }
      : { key: MAIL_CALENDAR_SEND_KEYS.nextRun, values: { time } };
  }
  return {
    key: MAIL_CALENDAR_SEND_KEYS.at,
    values: { date: `${pad2(at.getDate())}.${pad2(at.getMonth() + 1)}`, time },
  };
};

// ---------------------------------------------------------------------------
// Журнал: переменные «как ушли»
// ---------------------------------------------------------------------------

export interface IAsSentVariable {
  name: string;
  value: string;
  /** Абсолютная http(s)-ссылка — кликается в новой вкладке. */
  isLink: boolean;
}

const LINK_RE = /^https?:\/\/\S+$/i;

/**
 * `dynamicData` (graphene `JSONString`) → строки «ключ → значение». Вложенное
 * значение — JSON одной строкой: сплющенный объект читался бы как два разных
 * поля шаблона.
 */
export const variablesAsSent = (raw: unknown): IAsSentVariable[] => {
  const data = parseJsonValue(raw);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  return Object.entries(data as Record<string, unknown>).map(
    ([name, value]) => {
      const text =
        value === null || value === undefined
          ? ''
          : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value);
      return { name, value: text, isLink: LINK_RE.test(text.trim()) };
    },
  );
};

// ---------------------------------------------------------------------------
// Имя шаблона
// ---------------------------------------------------------------------------

export interface ITemplateCatalogEntry {
  id: string;
  name: string;
  subject?: string | null;
}

export interface IResolvedTemplate {
  /** Имя: своё (снимок правила), иначе из SendGrid; нет нигде — `null`. */
  name: string | null;
  subject: string | null;
  /**
   * SendGrid ответил, а шаблона с таким id в нём нет. Пока список не пришёл
   * или SendGrid недоступен — `false`: «не найден» говорить не о чем.
   */
  missing: boolean;
}

/**
 * Имя и тема шаблона правила. У засеянных при выкате правил имени нет (его
 * читают из SendGrid только при сохранении в ERP), и экран показывал голый
 * id. Теперь — снимок правила, иначе живой список SendGrid, и лишь когда
 * нет нигде — id.
 */
export const resolveTemplate = (
  templateId: string | null | undefined,
  own: { name?: string | null; subject?: string | null },
  catalog: ReadonlyMap<string, ITemplateCatalogEntry> | null,
): IResolvedTemplate => {
  if (!templateId) return { name: null, subject: null, missing: false };
  const found = catalog ? catalog.get(templateId) : undefined;
  return {
    name: own.name || found?.name || null,
    subject: own.subject || found?.subject || null,
    missing: Boolean(catalog && catalog.size > 0 && !found && !own.name),
  };
};

// ---------------------------------------------------------------------------
// Фильтры журнала
// ---------------------------------------------------------------------------

export interface IRuleTemplateOption {
  id: string;
  name: string;
}

/**
 * «Шаблон» в фильтре журнала — шаблоны ПРАВИЛ, а не весь SendGrid: писем по
 * шаблону, который ни одно правило не шлёт, в журнале быть не может.
 */
export const ruleTemplateOptions = (
  rules: ReadonlyArray<Pick<IFundingMailRule, 'templateId' | 'templateName'>>,
): IRuleTemplateOption[] => {
  const seen = new Map<string, IRuleTemplateOption>();
  rules.forEach(({ templateId, templateName }) => {
    if (!templateId || seen.has(templateId)) return;
    seen.set(templateId, { id: templateId, name: templateName || templateId });
  });
  return Array.from(seen.values());
};

/** Every static i18n key this module can hand out — walked by the locale gate. */
export const MAIL_RULES_I18N_KEYS: string[] = [
  ...Object.values(MAIL_PHRASE_KEYS).filter(
    (key) => !MAIL_PLURAL_KEYS.includes(key),
  ),
  ...MAIL_PLURAL_KEYS.flatMap((key) =>
    PLURAL_FORMS.map((form) => `${key}_${form}`),
  ),
  ...Object.values(MAIL_CALENDAR_SEND_KEYS),
  ...AUTOMATCH_BLOCKS.map((code) => `${MR}.automatch.reasons.${code}`),
  AUTOMATCH_BLOCK_UNKNOWN_KEY,
  ...Object.values(MAIL_WHEN_KEYS),
  ...Object.values(MAIL_LOCK_KEYS),
  ...Object.values(MAIL_STATE_KEYS),
  ...MAIL_ERROR_CODES.map(errorKey),
  MAIL_ERROR_FORBIDDEN_KEY,
  MAIL_ERROR_UNKNOWN_KEY,
];
